import { debugLogger } from '../utils/debugLogger'

interface SignResponse {
  uploadUrl: string
  publicUrl: string
  headers?: Record<string, string>
}

export class StorageService {
  private workerBase = process.env.REACT_APP_UPLOAD_WORKER_URL

  private ensureConfigured() {
    if (!this.workerBase) {
      throw new Error('Upload service not configured. Set REACT_APP_UPLOAD_WORKER_URL to a signer/worker endpoint that returns { uploadUrl, publicUrl }.')
    }
  }

  async signUpload(fileName: string, contentType: string): Promise<SignResponse> {
    this.ensureConfigured()
    const url = `${this.workerBase!.replace(/\/$/, '')}/sign`
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fileName, contentType })
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`Failed to get upload URL (${res.status}): ${text}`)
    }
    return await res.json()
  }

  async uploadFile(file: File): Promise<string> {
    debugLogger.log('StorageService', 'Signing upload', { fileName: file.name, type: file.type, size: file.size })
    const { uploadUrl, publicUrl, headers } = await this.signUpload(file.name, file.type || 'application/octet-stream')

    debugLogger.log('StorageService', 'Uploading to signed URL', { uploadUrl, hasHeaders: !!headers })
    const put = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream', ...(headers || {}) },
      body: file
    })
    if (!put.ok) {
      const text = await put.text().catch(() => '')
      throw new Error(`Upload failed (${put.status}): ${text}`)
    }

    debugLogger.log('StorageService', 'Upload complete', { publicUrl })
    return publicUrl
  }

  async uploadFiles(files: File[]): Promise<string[]> {
    const results: string[] = []
    for (const f of files) {
      const url = await this.uploadFile(f)
      results.push(url)
    }
    return results
  }

  guessMessageTypeFromUrls(urls: string[]): 'text' | 'image' | 'video' | 'file' {
    const first = (urls[0] || '').toLowerCase()
    if (first.match(/\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/)) return 'image'
    if (first.match(/\.(mp4|webm|mov|m4v|avi|mkv)(\?|$)/)) return 'video'
    return 'file'
  }
}

export const storageService = new StorageService()
